/* biome-ignore-all lint/suspicious/noExplicitAny: legacy table data handling */
import { useState, useRef, useEffect } from "react"
import { Table, Tag, Typography, Tooltip, Button, type TablePaginationConfig, type TableProps } from "antd"
import { CaretRightOutlined, PauseOutlined, EyeOutlined, CustomerServiceOutlined } from "@ant-design/icons"
import type React from "react"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import type { SpeechAssessmentLogItem } from "@/services/log/speechAssessmentApi"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import dayjs from "dayjs"
import { CDN_URL } from "@/configs/config"

const { Text } = Typography

interface SpeechAssessmentTableProps {
  data: SpeechAssessmentLogItem[]
  loading: boolean
  onViewDetail: (record: SpeechAssessmentLogItem) => void
  pagination?: TableProps["pagination"]
}

export const SpeechAssessmentTable: React.FC<SpeechAssessmentTableProps> = ({
  data,
  loading,
  onViewDetail,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  // Mini audio player state management (singleton player for the table)
  const [playingId, setPlayingId] = useState<number | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const resolveAudioUrl = (record: SpeechAssessmentLogItem): string | null => {
    if (record.audio_url) return record.audio_url
    if (record.audio_path) {
      if (record.audio_path.startsWith("http://") || record.audio_path.startsWith("https://")) {
        return record.audio_path
      }
      const cleanCdn = CDN_URL.replace(/\/+$/, "")
      const cleanPath = record.audio_path.replace(/^\/+/, "")
      return `${cleanCdn}/${cleanPath}`
    }
    return null
  }

  const handleTogglePlay = (e: React.MouseEvent, record: SpeechAssessmentLogItem) => {
    e.stopPropagation()
    const url = resolveAudioUrl(record)
    if (!url) return

    if (playingId === record.id) {
      // Pause currently playing audio
      if (audioRef.current) {
        audioRef.current.pause()
      }
      setPlayingId(null)
    } else {
      // Stop previous audio if any
      if (audioRef.current) {
        audioRef.current.pause()
      }

      const audio = new Audio(url)
      audioRef.current = audio

      audio.onended = () => {
        setPlayingId(null)
      }
      audio.onerror = () => {
        setPlayingId(null)
      }

      audio
        .play()
        .then(() => {
          setPlayingId(record.id)
        })
        .catch(() => {
          setPlayingId(null)
        })
    }
  }

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return "—"
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const getScoreBadge = (score?: number | string | null) => {
    if (score === null || score === undefined || score === "") {
      return <Text type="secondary">—</Text>
    }
    const num = Number(score)
    let color = "red"
    if (num >= 80) color = "green"
    else if (num >= 50) color = "gold"

    return (
      <Tag color={color} className="m-0 px-2 py-0.5 font-semibold text-xs">
        {num.toFixed(1)}
      </Tag>
    )
  }

  const columns: TableProps<SpeechAssessmentLogItem>["columns"] = [
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      fixed: "left",
      width: 130,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Người dùng",
      key: "user",
      width: 190,
      fixed: "left",
      render: (_, record: SpeechAssessmentLogItem) => (
        <UserCellLink
          user={record.user}
          userId={record.user_id ?? undefined}
          fallback={<Tag color="default">Khách vãng lai</Tag>}
        />
      ),
    },
    {
      title: "Từ / câu mẫu",
      dataIndex: "reference_text",
      key: "reference_text",
      width: 180,
      render: (text: string) => (
        <div className="line-clamp-2 break-words font-semibold text-gray-900" title={text}>
          {text || "—"}
        </div>
      ),
    },
    {
      title: "Ghi âm",
      key: "audio",
      width: 90,
      align: "center",
      render: (_, record: SpeechAssessmentLogItem) => {
        const audioUrl = resolveAudioUrl(record)
        if (!audioUrl) {
          return (
            <Tooltip title="Không có file âm thanh">
              <Button type="text" size="small" disabled icon={<CustomerServiceOutlined />} />
            </Tooltip>
          )
        }

        const isPlaying = playingId === record.id
        return (
          <Tooltip title={isPlaying ? "Tạm dừng phát" : "Nghe thử file ghi âm"}>
            <Button
              type={isPlaying ? "primary" : "default"}
              shape="circle"
              size="small"
              icon={isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
              onClick={(e) => handleTogglePlay(e, record)}
              className={isPlaying ? "animate-pulse" : ""}
            />
          </Tooltip>
        )
      },
    },
    {
      title: "Thời lượng",
      dataIndex: "audio_duration",
      key: "audio_duration",
      width: 105,
      align: "center",
      render: (duration: number | string | null, record: SpeechAssessmentLogItem) => {
        const durText = duration ? `${Number(duration).toFixed(1)}s` : "—"
        const sizeText = formatFileSize(record.audio_size)
        return (
          <Tooltip title={`Dung lượng: ${sizeText}`}>
            <div className="flex cursor-default flex-col font-mono text-xs">
              <span className="font-medium text-gray-800">{durText}</span>
              <Text type="secondary" className="text-[10px]">
                {sizeText}
              </Text>
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: "Điểm số",
      dataIndex: "pron_score",
      key: "pron_score",
      width: 95,
      align: "center",
      render: (score: number | string | null) => getScoreBadge(score),
    },
    {
      title: "Độ trễ",
      dataIndex: "latency_ms",
      key: "latency_ms",
      width: 100,
      align: "center",
      render: (ms: number | null) => {
        if (!ms) return <Text type="secondary">—</Text>
        const isVerySlow = ms >= 3000
        const isSlow = ms >= 1500
        const formatted = ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`

        let colorClass = "text-gray-700"
        if (isVerySlow) colorClass = "text-red-600 font-bold"
        else if (isSlow) colorClass = "text-amber-600 font-semibold"

        return (
          <Tooltip title={isVerySlow ? "Độ trễ cao (> 3000ms)" : undefined}>
            <span className={`font-mono text-xs ${colorClass}`}>{formatted}</span>
          </Tooltip>
        )
      },
    },
    {
      title: "Thiết bị",
      key: "device",
      width: 105,
      align: "center",
      render: (_, record: SpeechAssessmentLogItem) => <DeviceBadge userAgent={record.user_agent} />,
    },
    {
      title: "Trạng thái",
      dataIndex: "is_success",
      key: "is_success",
      width: 110,
      align: "center",
      render: (isSuccess: number | boolean, record: SpeechAssessmentLogItem) => {
        const ok = isSuccess === 1 || isSuccess === true
        return (
          <Tooltip title={!ok && record.error_message ? record.error_message : undefined}>
            <Tag color={ok ? "success" : "error"} className="m-0">
              {ok ? "Thành công" : "Thất bại"}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: "Thao tác",
      key: "action",
      width: 90,
      fixed: "right",
      align: "center",
      render: (_, record: SpeechAssessmentLogItem) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined className="text-blue-500" />}
          onClick={(e) => {
            e.stopPropagation()
            onViewDetail(record)
          }}
        >
          Chi tiết
        </Button>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<SpeechAssessmentLogItem>
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        onRow={(record) => ({
          onClick: () => onViewDetail(record),
          className: "cursor-pointer hover:bg-blue-50/40 transition-colors",
        })}
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

SpeechAssessmentTable.displayName = "SpeechAssessmentTable"

export default SpeechAssessmentTable
